<template>
  <div class="company-settings-page">
    <div class="pagetitle">
      <h1>⚙️ {{ $t('companySettings.title') }}</h1>
      <p class="subtitle">{{ $t('companySettings.subtitle') }}</p>
    </div>

    <div v-if="loading" class="loading-state">
      <p>{{ $t('common.loading') }}</p>
    </div>

    <div v-else class="settings-container">
      <form @submit.prevent="saveSettings">
        <!-- Working Hours Section -->
        <div class="settings-section">
          <h2>⏰ {{ $t('companySettings.workingHours') }}</h2>
          
          <div class="form-row">
            <div class="form-group">
              <label for="working_hours_per_week">{{ $t('companySettings.standardHoursPerWeek') }}</label>
              <input
                id="working_hours_per_week"
                v-model.number="form.working_hours_per_week"
                type="number"
                step="0.5"
                min="0"
                max="60"
                required
              />
              <small class="hint">{{ $t('companySettings.standardHoursHint') }}</small>
            </div>

            <div class="form-group">
              <label for="max_hours_per_week">{{ $t('companySettings.maxHoursPerWeek') }}</label>
              <input
                id="max_hours_per_week"
                v-model.number="form.max_hours_per_week"
                type="number"
                step="0.5"
                min="0"
                max="80"
                required
              />
              <small class="hint">{{ $t('companySettings.maxHoursHint') }}</small>
            </div>
          </div>
        </div>

        <!-- OB Times Section -->
        <div class="settings-section">
          <h2>🌙 {{ $t('companySettings.obTimes') }}</h2>
          
          <!-- OB1 Evening -->
          <div class="ob-period">
            <h3>{{ $t('companySettings.ob1Evening') }}</h3>
            <div class="form-row">
              <div class="form-group">
                <label for="ob1_start">{{ $t('companySettings.startTime') }}</label>
                <input
                  id="ob1_start"
                  v-model="form.ob1_start"
                  type="time"
                  required
                />
              </div>
              <div class="form-group">
                <label for="ob1_end">{{ $t('companySettings.endTime') }}</label>
                <input
                  id="ob1_end"
                  v-model="form.ob1_end"
                  type="time"
                  required
                />
              </div>
            </div>
            <small class="hint">{{ $t('companySettings.ob1Hint') }}</small>
          </div>

          <!-- OB2 Night -->
          <div class="ob-period">
            <h3>{{ $t('companySettings.ob2Night') }}</h3>
            <div class="form-row">
              <div class="form-group">
                <label for="ob2_start">{{ $t('companySettings.startTime') }}</label>
                <input
                  id="ob2_start"
                  v-model="form.ob2_start"
                  type="time"
                  required
                />
              </div>
              <div class="form-group">
                <label for="ob2_end">{{ $t('companySettings.endTime') }}</label>
                <input
                  id="ob2_end"
                  v-model="form.ob2_end"
                  type="time"
                  required
                />
              </div>
            </div>
            <small class="hint">{{ $t('companySettings.ob2Hint') }}</small>
          </div>

          <!-- OB3 Weekend -->
          <div class="ob-period">
            <h3>{{ $t('companySettings.ob3Weekend') }}</h3>
            <div class="form-row">
              <div class="form-group">
                <label for="ob3_start_day">{{ $t('companySettings.startDay') }}</label>
                <select id="ob3_start_day" v-model.number="form.ob3_start_day" required>
                  <option :value="0">{{ $t('weekdays.sunday') }}</option>
                  <option :value="1">{{ $t('weekdays.monday') }}</option>
                  <option :value="2">{{ $t('weekdays.tuesday') }}</option>
                  <option :value="3">{{ $t('weekdays.wednesday') }}</option>
                  <option :value="4">{{ $t('weekdays.thursday') }}</option>
                  <option :value="5">{{ $t('weekdays.friday') }}</option>
                  <option :value="6">{{ $t('weekdays.saturday') }}</option>
                </select>
              </div>
              <div class="form-group">
                <label for="ob3_start_time">{{ $t('companySettings.startTime') }}</label>
                <input
                  id="ob3_start_time"
                  v-model="form.ob3_start_time"
                  type="time"
                  required
                />
              </div>
            </div>
            <div class="form-row">
              <div class="form-group">
                <label for="ob3_end_day">{{ $t('companySettings.endDay') }}</label>
                <select id="ob3_end_day" v-model.number="form.ob3_end_day" required>
                  <option :value="0">{{ $t('weekdays.sunday') }}</option>
                  <option :value="1">{{ $t('weekdays.monday') }}</option>
                  <option :value="2">{{ $t('weekdays.tuesday') }}</option>
                  <option :value="3">{{ $t('weekdays.wednesday') }}</option>
                  <option :value="4">{{ $t('weekdays.thursday') }}</option>
                  <option :value="5">{{ $t('weekdays.friday') }}</option>
                  <option :value="6">{{ $t('weekdays.saturday') }}</option>
                </select>
              </div>
              <div class="form-group">
                <label for="ob3_end_time">{{ $t('companySettings.endTime') }}</label>
                <input
                  id="ob3_end_time"
                  v-model="form.ob3_end_time"
                  type="time"
                  required
                />
              </div>
            </div>
            <small class="hint">{{ $t('companySettings.ob3Hint') }}</small>
          </div>
        </div>

        <!-- Shift Constraints Section -->
        <div class="settings-section">
          <h2>🔒 {{ $t('companySettings.shiftConstraints') }}</h2>
          
          <div class="form-row">
            <div class="form-group">
              <label for="min_rest_hours">{{ $t('companySettings.minRestHours') }}</label>
              <input
                id="min_rest_hours"
                v-model.number="form.min_rest_hours"
                type="number"
                step="0.5"
                min="8"
                max="24"
                required
              />
              <small class="hint">{{ $t('companySettings.minRestHint') }}</small>
            </div>

            <div class="form-group">
              <label for="min_shift_for_break">{{ $t('companySettings.minShiftForBreak') }}</label>
              <input
                id="min_shift_for_break"
                v-model.number="form.min_shift_for_break"
                type="number"
                step="0.5"
                min="4"
                max="8"
                required
              />
              <small class="hint">{{ $t('companySettings.minShiftBreakHint') }}</small>
            </div>

            <div class="form-group">
              <label for="default_break_duration">{{ $t('companySettings.defaultBreakDuration') }}</label>
              <input
                id="default_break_duration"
                v-model.number="form.default_break_duration"
                type="number"
                step="5"
                min="15"
                max="60"
                required
              />
              <small class="hint">{{ $t('companySettings.breakDurationHint') }}</small>
            </div>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="form-actions">
          <button type="button" @click="resetForm" class="btn-secondary" :disabled="saving">
            {{ $t('common.cancel') }}
          </button>
          <button type="submit" class="btn-primary" :disabled="saving">
            {{ saving ? $t('common.saving') : $t('common.save') }}
          </button>
        </div>
      </form>

      <!-- Success/Error Messages -->
      <div v-if="successMessage" class="success-message">
        ✅ {{ successMessage }}
      </div>
      <div v-if="errorMessage" class="error-message">
        ❌ {{ errorMessage }}
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import * as companySettingsApi from '../services/companySettingsApi.js'

const { t } = useI18n()

const loading = ref(true)
const saving = ref(false)
const successMessage = ref('')
const errorMessage = ref('')

const form = ref({
  working_hours_per_week: 40.0,
  max_hours_per_week: 48.0,
  ob1_start: '18:15',
  ob1_end: '20:00',
  ob2_start: '20:00',
  ob2_end: '05:00',
  ob3_start_day: 6, // Saturday
  ob3_start_time: '12:00',
  ob3_end_day: 1, // Monday
  ob3_end_time: '05:00',
  min_rest_hours: 11.0,
  min_shift_for_break: 5.5,
  default_break_duration: 30
})

const originalSettings = ref({})

async function loadSettings() {
  loading.value = true
  errorMessage.value = ''
  
  try {
    const settings = await companySettingsApi.getSettings()
    form.value = { ...settings }
    originalSettings.value = { ...settings }
  } catch (error) {
    console.error('Failed to load settings:', error)
    errorMessage.value = error.message || 'Failed to load company settings'
  } finally {
    loading.value = false
  }
}

async function saveSettings() {
  saving.value = true
  successMessage.value = ''
  errorMessage.value = ''
  
  try {
    const updated = await companySettingsApi.updateSettings(form.value)
    form.value = { ...updated }
    originalSettings.value = { ...updated }
    
    successMessage.value = t('companySettings.saveSuccess')
    
    // Clear success message after 3 seconds
    setTimeout(() => {
      successMessage.value = ''
    }, 3000)
  } catch (error) {
    console.error('Failed to save settings:', error)
    errorMessage.value = error.message || 'Failed to save company settings'
  } finally {
    saving.value = false
  }
}

function resetForm() {
  form.value = { ...originalSettings.value }
  successMessage.value = ''
  errorMessage.value = ''
}

onMounted(() => {
  loadSettings()
})
</script>

<style scoped>
.company-settings-page {
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
}

.page-header {
  margin-bottom: 2rem;
}

.page-header h1 {
  font-size: 1.75rem;
  color: #2c3e50;
  margin-bottom: 0.5rem;
}

.pagetitle h1 {
  font-size: 2rem;
  color: #f0f0f0;
  margin-bottom: 0.5rem;
}

.subtitle {
  color: #dadada;
  font-size: 1rem;
  margin-bottom: 1rem;
}

.loading-state {
  text-align: center;
  padding: 3rem;
  color: #dadada;
}

.settings-container {
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.settings-section {
  margin-bottom: 3rem;
  padding-bottom: 2rem;
  border-bottom: 2px solid #f0f0f0;
}

.settings-section:last-of-type {
  border-bottom: none;
}

.settings-section h2 {
  font-size: 1.5rem;
  color: #2c3e50;
  margin-bottom: 1.5rem;
}

.ob-period {
  background: #f8f9fa;
  padding: 1.5rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
}

.ob-period h3 {
  font-size: 1.2rem;
  color: #495057;
  margin-bottom: 1rem;
}

.form-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 1rem;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group label {
  font-weight: 600;
  color: #495057;
  margin-bottom: 0.5rem;
  font-size: 0.95rem;
}

.form-group input,
.form-group select {
  padding: 0.75rem;
  border: 2px solid #dee2e6;
  border-radius: 6px;
  font-size: 1rem;
  transition: border-color 0.2s;
}

.form-group input:focus,
.form-group select:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
}

.hint {
  color: #6c757d;
  font-size: 0.85rem;
  margin-top: 0.25rem;
  font-style: italic;
}

.form-actions {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 2px solid #f0f0f0;
}

.btn-primary,
.btn-secondary {
  padding: 0.75rem 2rem;
  border: none;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-primary {
  background: #007bff;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #0056b3;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 123, 255, 0.2);
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.btn-secondary:hover:not(:disabled) {
  background: #545b62;
}

.btn-primary:disabled,
.btn-secondary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.success-message,
.error-message {
  margin-top: 1.5rem;
  padding: 1rem;
  border-radius: 6px;
  font-weight: 500;
}

.success-message {
  background: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.error-message {
  background: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

@media (max-width: 768px) {
  .company-settings-page {
    padding: 1rem;
  }

  .form-row {
    grid-template-columns: 1fr;
  }

  .form-actions {
    flex-direction: column;
  }

  .btn-primary,
  .btn-secondary {
    width: 100%;
  }
}
</style>
